import {
    type ComponentType,
    lazy,
    SetStateAction,
    Suspense,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    ApolloClient,
    ApolloLink,
    HttpLink,
    InMemoryCache,
    Operation,
    NextLink
    // @ts-ignore - @apollo/client is ESM but this file is treated as CommonJS by TypeScript
} from "@apollo/client";
import { v4 as uuidv4 } from "uuid";

import CommentIcon from "@atlaskit/icon/core/comment";
import Spinner from "@atlaskit/spinner";
import { token } from "@atlaskit/tokens";

// Load omni-channel UI only on the client when widget is open to avoid SSR 503 (Tesseract)
const ChatWidgetOmniChannelUI = lazy(
    () =>
        // @ts-expect-error - extension-less path; bundler resolves correctly
        import("./chat-widget-omni-channel-ui") as unknown as Promise<{
            default: ComponentType<ChatWidgetOmniChannelUIProps>;
        }>
);

import { wacToken } from "@/libraries/design/foundations";
import { ClientOnly } from "@/utils/client-only";
import { Cookie } from "@/utils/cookie";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import {
    getChannelPlatformTokenByClientName,
    getChatConnectionConfigForSalesChannel,
    getCustomerConversations
} from "./ocp-gql";

import type { ChatWidgetOmniChannelUIProps } from "./chat-widget-omni-channel-ui";

/**
 * Converts a full WAC URL to a relative path segment (no domain, no hash).
 * - Strips optional leading /wac from the path.
 * - Examples:
 *   - https://...atlassian.com/wac/licensing/purchase-licensing#trials -> licensing/purchase-licensing
 *   - https://wac.stg.../software/compass/features -> software/compass/features
 */
export const getRelativePathFromWacUrl = (url: string): string => {
    try {
        const urlObj = new URL(url);
        let pathname = urlObj.pathname.replace(/\/+$/, "");

        if (pathname.startsWith("/wac/")) {
            pathname = pathname.slice("/wac".length);
        }
        return pathname.replace(/^\/+/, "");
    } catch {
        const match = /\/\/[^/]+(\/[^#?]*)/.exec(url);
        if (match?.[1]) {
            let pathname = match[1].replace(/\/+$/, "");
            if (pathname.startsWith("/wac/")) {
                pathname = pathname.slice("/wac".length);
            }
            return pathname.replace(/^\/+/, "");
        }
        return "";
    }
};

const PATH_TO_CONTACT_FLOW_ID: Record<string, string> = {
    "software/jira/product-discovery":
        process.env["JIRA_PRODUCT_DISCOVERY_CONTACT_FLOW_ID"] ||
        "4cdb6e29-9088-4404-bbfe-dfdba35bd3d6",
    "software/jira":
        process.env["JIRA_CONTACT_FLOW_ID"] ||
        "7e776619-ede4-4975-a194-38c6b182fa0c",
    "software/loom":
        process.env["LOOM_CONTACT_FLOW_ID"] ||
        "42cee94e-c0db-43d2-bd45-3cca9d7adb61",
    "software/loom/loom-confluence":
        process.env["LOOM_CONFLUENCE_CONTACT_FLOW_ID"] ||
        "9f1756b6-8607-43b4-97b6-32f888713bf0",
    "software/loom/loom-jira":
        process.env["LOOM_JIRA_CONTACT_FLOW_ID"] ||
        "863e7f31-092a-45c6-affa-02db50c7ef72",
    "software/loom/resources":
        process.env["LOOM_RESOURCES_CONTACT_FLOW_ID"] ||
        "4b5e7c93-5e16-4479-8030-fcad737249a3",
    "software/loom/ai-meeting":
        process.env["LOOM_AI_MEETING_CONTACT_FLOW_ID"] ||
        "f1956561-00b8-4ee0-a6c2-2100d07feb5f",
    "software/loom/editing":
        process.env["LOOM_EDITING_CONTACT_FLOW_ID"] ||
        "bc21d930-ac0d-4eec-9158-d2dde2097029"
};

export interface ContactFlowResult {
    contactFlowId: string;
}

/**
 * Returns an object with contactFlowId for a given relative path (e.g. "software/jira/product-discovery").
 */
export const getContactFlowByPath = (path: string): ContactFlowResult => {
    const normalizedPath = path.replace(/^\/+|\/+$/g, "");
    console.log("normalizedPath : ", normalizedPath);
    console.log("contactFlowId : ", PATH_TO_CONTACT_FLOW_ID[normalizedPath]);
    return {
        contactFlowId: PATH_TO_CONTACT_FLOW_ID[normalizedPath] ?? ""
    };
};

export const convertUrlToSnakeCase = (url: string): string => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;

        // Remove leading and trailing slashes, then split by '/' and filter out empty strings
        const pathParts = pathname
            .replace(/^\/+|\/+$/g, "")
            .split("/")
            .filter((part) => part.length > 0);

        // Join with underscores
        return pathParts.join("_");
    } catch (error) {
        // If URL parsing fails, try to extract path manually
        const match = /\/\/[^\/]+(\/[^?#]*)/.exec(url);
        if (match && match[1]) {
            const pathname = match[1];
            const pathParts = pathname
                .replace(/^\/+|\/+$/g, "")
                .split("/")
                .filter((part) => part.length > 0);
            return pathParts.join("_");
        }
        return "";
    }
};

export const transformPayloadForChannelPlatform = (
    payload: Record<string, unknown>
): Record<string, string[]> => {
    const transformed: Record<string, string[]> = {};
    if (!payload) {
        return transformed;
    }

    Object.entries(payload).forEach(([key, value]) => {
        // Skip attachments
        if (key === "attachments") {
            return;
        }

        // Skip null or undefined values
        if (value === null || value === undefined) {
            return;
        }

        // Handle arrays - normalize to array of strings
        if (Array.isArray(value)) {
            transformed[key] = value.map((v) => {
                // If array element is an object (like {userEmail: "value"}), extract the first value
                if (typeof v === "object" && v !== null && !Array.isArray(v)) {
                    const firstValue = Object.values(v)[0];
                    return firstValue !== undefined
                        ? String(firstValue)
                        : String(v);
                }
                // Already a string or primitive
                return String(v);
            });
        }
        // Handle single values - convert to string and wrap in array
        else {
            transformed[key] = [String(value)];
        }
    });
    return transformed;
};

export const constructMetadata = (payload: Record<string, unknown>) => {
    return JSON.stringify(payload);
};

export const transformChatContactDetailsForSalesChannel =
    (chatConnectionConfigResponse: {
        channelPlatform_createContact?: unknown;
    }) => {
        const submitRequest =
            chatConnectionConfigResponse?.channelPlatform_createContact;

        return {
            channelPlatform_submitRequest: submitRequest
        };
    };

export type SurveyModalAttributes = {
    width?: string | number;
    height?: string | number;
    top?: string | number;
    left?: string | number;
};

export const ChatWidgetProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const [clientToken, setClientToken] = useState<string | null>(null);
    const [chatConnectionConfig, setChatConnectionConfig] = useState<{
        channelPlatform_createContact?: unknown;
    } | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [isWidgetOpen, setIsWidgetOpen] = useState<boolean>(false);
    const [customerConversationsResponse, setCustomerConversationsResponse] =
        useState<{
            channelPlatform_getCustomerConversations?: {
                conversations?: unknown[];
            };
        } | null>(null);
    const {
        accountId: aaid,
        email: userEmail,
        isUserLoggedIn
    } = useLoggedInUser();

    // Generate a unique context ID once per component instance
    const contextId = useMemo(() => uuidv4(), []);

    const generateAuthHeader = (channelsIdentityHash: string) =>
        new ApolloLink((operation: Operation, forward: NextLink) => {
            operation.setContext(({ headers = {} }) => ({
                headers: {
                    ...headers,
                    "Content-Type": "application/json",
                    "X-Client-Hash": channelsIdentityHash
                }
            }));

            return forward(operation);
        });

    const client = useMemo(() => {
        const link = new HttpLink({
            uri:
                process.env["REACT_APP_AGG_GRAPHQL_BASE_URL"] ||
                "https://support.stg.internal.atlassian.com/gateway/api/graphql",
            credentials: "include"
        });

        return new ApolloClient({
            link: link,
            cache: new InMemoryCache()
        });
    }, []);

    const wasWidgetOpenRef = useRef(false);

    const clientWithToken = useMemo(() => {
        if (!clientToken) {
            return null;
        }
        const link = new HttpLink({
            uri:
                process.env["REACT_APP_AGG_GRAPHQL_BASE_URL"] ||
                "https://support.stg.internal.atlassian.com/gateway/api/graphql",
            credentials: "include"
        });

        return new ApolloClient({
            link: ApolloLink.from([generateAuthHeader(clientToken), link]),
            cache: new InMemoryCache()
        });
    }, [clientToken]);

    const isRefreshed = useMemo(() => {
        return (
            !!customerConversationsResponse &&
            (customerConversationsResponse
                .channelPlatform_getCustomerConversations?.conversations
                ?.length ?? 0) > 0
        );
    }, [customerConversationsResponse]);

    const fetchCustomerConversations = () => {
        if (!clientWithToken) return;
        const anonymousId = Cookie.readCookie("ajs_anonymous_id") || "";
        clientWithToken
            .query({
                query: getCustomerConversations,
                variables: { anonymousId }
            })
            .then((response) => {
                setCustomerConversationsResponse(response?.data ?? null);
            })
            .catch((err) => {
                console.warn("Error fetching customer conversations:", err);
                setCustomerConversationsResponse(null);
            });
    };

    const fetchChatConnectionConfig = () => {
        if (!clientWithToken) {
            console.warn("Client with token not available yet");
            return;
        }

        setLoading(true);
        const formValues = {
            cst_support_forms_data_fieldDescMap: [
                '{"inquiry_category":"Inquiry Category","email":"Email","raised_obo_by":"Raised on behalf of by","language":"language","customer_working_hours":"Support Business Hours Select"}'
            ],
            customer_email: [isUserLoggedIn ? userEmail : ""],
            authenticated: isUserLoggedIn ? null : ["No"],
            email: [isUserLoggedIn ? userEmail : ""],
            customer_status: ["Customer"],
            cst_support_forms_data_formKey: [
                "ca-growth-pricing-billing-and-licensing"
            ],
            cst_support_forms_data_formLabel: [
                "ca-growth-pricing-billing-and-licensing"
            ],
            cst_support_forms_data_timezone: ["GMT+6"],
            referral_url: [window.location.href],
            language: ["en"],
            is_partner_dc_exception: ["Customer not in SFDC"],
            topic: [convertUrlToSnakeCase(window.location.href)],
            summary: [
                convertUrlToSnakeCase(window.location.href).split("_").join(" ")
            ],
            support_channel: ["chat"],
            Tag_Chat_Expansion: ["SSA_Bot_Expansion"],
            inquiry_category: ["access_issues"],
            cst_support_forms_data_contextId: [contextId]
        };

        clientWithToken
            .query({
                query: getChatConnectionConfigForSalesChannel,
                variables: {
                    requestType: "CHAT" as "CHAT",
                    payload: transformPayloadForChannelPlatform(formValues),
                    metadata: constructMetadata({
                        flowId: getContactFlowByPath(
                            getRelativePathFromWacUrl(window.location.href)
                        ).contactFlowId,
                        formContext: {
                            summary: [
                                "Chat Transcript for <email removed for privacy>"
                            ],
                            details: "",
                            contactEmail: isUserLoggedIn ? userEmail : "",
                            isAuthenticated: isUserLoggedIn ? true : false
                        }
                    }),
                    anonymousId: Cookie.readCookie("ajs_anonymous_id") || ""
                }
            })
            .then(
                (response: {
                    data: { channelPlatform_createContact?: unknown };
                }) => {
                    setChatConnectionConfig(response?.data);
                    console.log("Chat response:", response);
                    setLoading(false);
                }
            )
            .catch((err: SetStateAction<Error | null>) => {
                console.log("Error fetching chat connection config:", err);
                setError(err);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (
            isWidgetOpen &&
            !wasWidgetOpenRef.current &&
            clientToken &&
            clientWithToken
        ) {
            wasWidgetOpenRef.current = true;
            fetchCustomerConversations();
            fetchChatConnectionConfig();
        }
        if (!isWidgetOpen) {
            wasWidgetOpenRef.current = false;
        }
    }, [isWidgetOpen, clientToken, clientWithToken]);

    const surveyModalAttributes: SurveyModalAttributes = {
        width: "80%",
        height: 500
    };

    const omniChannelEnv: "production" | "staging" =
        process.env["OMNI_CHANNEL_ENVIRONMENT"] === "production"
            ? "production"
            : "staging";

    const handleChatButtonClick = () => {
        setIsWidgetOpen(!isWidgetOpen);
        if (!isWidgetOpen) {
            setLoading(true);
            client
                .query({
                    query: getChannelPlatformTokenByClientName,
                    variables: { clientName: "sales" }
                })
                .then(
                    (response: {
                        data: {
                            supportInquiry_channelsIdentityHashByClientName: string;
                        };
                    }) => {
                        const token =
                            response.data
                                ?.supportInquiry_channelsIdentityHashByClientName;
                        if (token) {
                            setClientToken(token);
                        }
                        setLoading(false);
                    }
                )
                .catch((err: SetStateAction<Error | null>) => {
                    console.error(
                        "Error fetching channel platform token:",
                        err
                    );
                    setError(err);
                    setLoading(false);
                });
        }
    };

    const handleChatButtonKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleChatButtonClick();
        }
    };

    return (
        <ClientOnly>
            <>
                {children}
                {!isWidgetOpen && (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Open chat widget"
                        style={{
                            position: "fixed",
                            bottom: "20px",
                            right: "20px",
                            zIndex: 1000,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            backgroundColor: wacToken("color.primary.blue"),
                            color: "white",
                            boxShadow: token("elevation.shadow.raised")
                        }}
                        onClick={handleChatButtonClick}
                        onKeyDown={handleChatButtonKeyDown}
                    >
                        <CommentIcon
                            label=""
                            size="medium"
                            color="currentColor"
                        />
                    </div>
                )}
                {isWidgetOpen && (!clientToken || !chatConnectionConfig) && (
                    <div
                        style={{
                            position: "fixed",
                            bottom: 20,
                            right: 20,
                            width: "400px",
                            height: "700px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: wacToken("color.neutral.light0"),
                            borderRadius: "8px",
                            boxShadow: token("elevation.shadow.overlay"),
                            zIndex: 999
                        }}
                    >
                        <Spinner size="large" label="Loading chat" />
                    </div>
                )}
                {isWidgetOpen && chatConnectionConfig && clientToken && (
                    <Suspense
                        fallback={
                            <div
                                style={{
                                    position: "fixed",
                                    bottom: 20,
                                    right: 20,
                                    width: "400px",
                                    height: "700px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: wacToken(
                                        "color.neutral.light0"
                                    ),
                                    borderRadius: "8px",
                                    boxShadow: token(
                                        "elevation.shadow.overlay"
                                    ),
                                    zIndex: 999
                                }}
                            >
                                <Spinner size="large" label="Loading chat" />
                            </div>
                        }
                    >
                        <ChatWidgetOmniChannelUI
                            env={
                                omniChannelEnv as ChatWidgetOmniChannelUIProps["env"]
                            }
                            clientIdentityHash={clientToken || ""}
                            chatContactDetails={transformChatContactDetailsForSalesChannel(
                                chatConnectionConfig
                            )}
                            accountId={aaid || ""}
                            isRefreshed={isRefreshed}
                            customerConversations={
                                customerConversationsResponse
                            }
                            loading={loading}
                            error={error}
                            isUserLoggedIn={isUserLoggedIn}
                            surveyModalAttributes={surveyModalAttributes}
                        />
                    </Suspense>
                )}
            </>
        </ClientOnly>
    );
};

export default ChatWidgetProvider;
